/**
 * @fileoverview helper กลางของกลุ่มเมนู "บันทึกเวลา" (ลงเวลาเข้า-ออก / Activity โครงการ / ประเภท Activity)
 * @module utils/timeTracking
 */
import { effectivePermissions } from "./portalAccess.js";

const TZ = "Asia/Bangkok";
// ช่วงรายงานสูงสุดที่ backend รับ (เกิน = 400)
export const MAX_RANGE_DAYS = 93;

const pad = (n) => String(n).padStart(2, "0");

/** Date -> "YYYY-MM-DD" ตาม local time (ไม่ใช้ toISOString เพราะเลื่อนตาม timezone) */
export const toDateOnly = (date) => {
  if (!date) return "";
  const d = new Date(date);
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

/** ช่วงสัปดาห์นี้: จันทร์ -> วันนี้ (ไม่เลยวันนี้ เพื่อไม่ให้ขึ้น "ไม่ลงเวลา" ของวันอนาคต) */
export const currentWeekRange = () => {
  const now = new Date();
  const monday = new Date(now);
  monday.setDate(now.getDate() - ((now.getDay() + 6) % 7));
  return { from: toDateOnly(monday), to: toDateOnly(now) };
};

/** จำนวนวันของช่วง (รวมวันแรกและวันสุดท้าย) จาก "YYYY-MM-DD" สองค่า */
export const rangeDays = (from, to) => {
  if (!from || !to) return 0;
  const a = new Date(`${from}T00:00:00Z`).getTime();
  const b = new Date(`${to}T00:00:00Z`).getTime();
  return Math.round((b - a) / 86400000) + 1;
};

/**
 * ตรวจช่วงวันที่ก่อนยิง API
 * @returns {""|"required"|"order"|"too_long"}
 */
export const validateRange = (from, to) => {
  if (!from || !to) return "required";
  if (from > to) return "order";
  if (rangeDays(from, to) > MAX_RANGE_DAYS) return "too_long";
  return "";
};

/** "YYYY-MM-DD" -> "DD/MM/YYYY" (ปี ค.ศ. ตามที่ทั้งระบบใช้) */
export const fmtDate = (value) => {
  if (!value) return "-";
  const [y, m, d] = String(value).slice(0, 10).split("-");
  if (!y || !m || !d) return "-";
  return `${d}/${m}/${y}`;
};

/** "YYYY-MM-DD" -> "DD/MM" */
export const fmtDayMonth = (value) => {
  if (!value) return "-";
  const [, m, d] = String(value).slice(0, 10).split("-");
  return m && d ? `${d}/${m}` : "-";
};

/** "YYYY-MM-DD" -> "DD/MM/YYYY" ปี ค.ศ. ทุกภาษา (ตามต้นแบบหัวรายละเอียด/คำขอแก้ไข) */
export const fmtDateLocal = (value, lang) => {
  if (!value) return "-";
  const [y, m, d] = String(value).slice(0, 10).split("-");
  if (!y || !m || !d) return "-";
  return `${d}/${m}/${y}`;
};

/** ISO datetime -> "HH:mm" เวลาไทย */
export const fmtTime = (iso) => {
  if (!iso) return "-";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "-";
  return new Intl.DateTimeFormat("en-GB", { hour: "2-digit", minute: "2-digit", hour12: false, timeZone: TZ }).format(d);
};

/** ISO datetime -> "DD/MM/YYYY HH:mm" เวลาไทย */
export const fmtDateTime = (iso) => {
  if (!iso) return "-";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "-";
  const parts = new Intl.DateTimeFormat("en-GB", {
    day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit", hour12: false, timeZone: TZ,
  }).formatToParts(d).reduce((acc, p) => ({ ...acc, [p.type]: p.value }), {});
  return `${parts.day}/${parts.month}/${parts.year} ${parts.hour}:${parts.minute}`;
};

/** ISO datetime -> "DD/MM HH:mm" เวลาไทย */
export const fmtDayMonthTime = (iso) => {
  const full = fmtDateTime(iso);
  return full === "-" ? "-" : `${full.slice(0, 5)} ${full.slice(11)}`;
};

/** นาที -> "H:mm" (ชั่วโมง:นาที) */
export const fmtMinutes = (min) => {
  if (min === null || min === undefined || min === "" || isNaN(Number(min))) return "-";
  const m = Math.max(0, Math.round(Number(min)));
  return `${Math.floor(m / 60)}:${pad(m % 60)}`;
};

/** ลิงก์เปิดแผนที่ (ไม่มีพิกัด = null) */
export const mapUrl = (lat, lng) => {
  if (lat === null || lat === undefined || lat === "" || lng === null || lng === undefined || lng === "") return null;
  return `https://www.google.com/maps?q=${lat},${lng}`;
};

/** ตัด query param ที่ว่างออก */
export const pruneParams = (params) =>
  Object.fromEntries(Object.entries(params).filter(([, v]) => v !== "" && v !== null && v !== undefined));

/** สิทธิ์ของผู้ใช้ปัจจุบัน - super_user / admin-company ข้ามเหมือน PermissionRoute */
export const hasPermission = (profile, key) => {
  if (["super_user", "admin-company"].includes(profile?.user_type)) return true;
  // พอร์ทัลที่ประเภทผู้ใช้ดูอย่างเดียว เหลือเฉพาะ .read (utils/portalAccess)
  return effectivePermissions(profile).includes(key);
};

/** ชื่อประเภท Activity ตามภาษาปัจจุบัน */
export const typeName = (type, lang) => {
  if (!type) return "-";
  const th = type.name_th;
  const en = type.name_en;
  return (lang === "en" ? en || th : th || en) || "-";
};
